import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import clsx from "clsx";
import { useNavigate, Link } from "react-router-dom";
import { useConfig } from "../providers/config-provider";
import { useNotification } from "../providers/notification-provider";
import { forgotPasswordRequest, verifyOtpRequest, resetPasswordRequest, fetchPublicInfo } from "../lib/api";
import { extractApiErrorMessage } from "../utils/apiErrorUtils";
import logo from "../assets/logo.png";
import { EnvelopeIcon, KeyIcon, LockClosedIcon, CheckCircleIcon, ShieldCheckIcon } from "@heroicons/react/24/outline";

const steps = [
  { id: 1, label: 'Identificação' },
  { id: 2, label: 'Código OTP' },
  { id: 3, label: 'Nova Senha' },
  { id: 4, label: 'Concluído' },
];

const emailSchema = z.object({
  email: z.string().email("Informe um email válido")
});

const otpSchema = z.object({
  otp: z.string().length(6, "O código deve ter 6 dígitos")
});

const passwordSchema = z.object({
  password: z.string()
    .min(8, "A senha deve ter no mínimo 8 caracteres")
    .regex(/[A-Za-z]/, "A senha deve conter ao menos uma letra")
    .regex(/\d/, "A senha deve conter ao menos um número"),
  confirmPassword: z.string()
}).refine((data) => data.password === data.confirmPassword, {
  message: "As senhas não conferem",
  path: ["confirmPassword"],
});

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const config = useConfig();
  const { showToast } = useNotification();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");

  useEffect(() => {
    let cancelled = false;
    fetchPublicInfo(config.apiBase)
      .then((res) => {
        if (!cancelled && res.data?.supportEmail) {
          setSupportEmail(res.data.supportEmail);
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [config.apiBase]);

  const [feedback, setFeedback] = useState({ type: "", message: "" });
  const [supportEmail, setSupportEmail] = useState(null);

  const setErrorFeedback = (message) => {
    setFeedback({ type: "error", message });
    showToast(message, "error");
  };

  const setSuccessFeedback = (message) => {
    setFeedback({ type: "success", message });
    showToast(message, "success");
  };

  const { register: regEmail, handleSubmit: handleEmail, formState: { errors: errEmail }, setError: setErrEmail } = useForm({ resolver: zodResolver(emailSchema) });
  const { register: regOtp, handleSubmit: handleOtp, formState: { errors: errOtp }, setError: setErrOtp } = useForm({ resolver: zodResolver(otpSchema) });
  const { register: regPw, handleSubmit: handlePw, formState: { errors: errPw }, setError: setErrPw } = useForm({ resolver: zodResolver(passwordSchema) });

  useEffect(() => { document.title = "Recuperar Senha | Orion WaiterCall"; }, []);

  const emailMutation = useMutation({
    mutationFn: async (values) => {
      await forgotPasswordRequest(config.apiBase, values.email);
      return values.email;
    },
    onSuccess: (emailResult) => {
      setEmail(emailResult);
      setStep(2);
      setSuccessFeedback("Se o usuário existir, um código foi enviado para o email cadastrado.");
    },
    onError: (error) => {
      const message = extractApiErrorMessage(error, "Não foi possível enviar o código agora. Tente novamente em instantes.");
      setErrEmail("email", { message });
      setErrorFeedback(message);
    }
  });

  const otpMutation = useMutation({
    mutationFn: async (values) => {
      const normalizedOtp = String(values.otp || "").replace(/\D/g, "").slice(0, 6);
      await verifyOtpRequest(config.apiBase, email, normalizedOtp);
      return normalizedOtp;
    },
    onSuccess: (otpResult) => {
      setOtp(otpResult);
      setStep(3);
      setSuccessFeedback("Código validado. Defina sua nova senha.");
    },
    onError: (error) => {
      const message = extractApiErrorMessage(error, "Código inválido ou expirado. Solicite um novo código e tente novamente.");
      setErrOtp("otp", { message });
      setErrorFeedback(message);
    }
  });

  const passwordMutation = useMutation({
    mutationFn: async (values) => {
      await resetPasswordRequest(config.apiBase, email, otp, values.password);
    },
    onSuccess: () => {
      setStep(4);
      setSuccessFeedback("Senha alterada com sucesso.");
    },
    onError: (error) => {
      const message = extractApiErrorMessage(error, "Não foi possível redefinir a senha agora. Tente novamente.");
      setErrPw("password", { message });
      setErrorFeedback(message);
    }
  });

  return (
    <div className="min-h-screen bg-gray-100 px-4 py-8 sm:px-6 lg:px-8 flex flex-col justify-center">
      <div className="mx-auto w-full max-w-md">
        <div className="mb-6 text-center">
          <img src={logo} alt="Orion WaiterCall" className="mx-auto h-auto w-24 sm:w-28" />
          <h1 className="mt-4 text-2xl font-bold tracking-tight text-gray-900">Recuperação de Senha</h1>
          <p className="mt-2 text-sm text-gray-600">
            Processo seguro para restaurar o acesso à sua conta.
          </p>
        </div>

        <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm sm:p-6">
          {feedback.message && (
            <div className={`mb-4 rounded-md p-4 text-sm border ${feedback.type === "error" ? "bg-red-50 text-red-800 border-red-200" : "bg-green-50 text-green-800 border-green-200"}`}>
              {feedback.message}
            </div>
          )}

          <div className="mb-6 grid grid-cols-4 gap-2">
            {steps.map((item) => {
              const isDone = step > item.id;
              const isCurrent = step === item.id;
              return (
                <div key={`step-${item.id}`} className="text-center">
                  <div
                    className={`mx-auto mb-1 flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold transition-colors ${
                      isDone || isCurrent
                        ? 'bg-indigo-600 text-white'
                        : 'bg-gray-200 text-gray-500'
                    }`}
                  >
                    {item.id}
                  </div>
                  <p className={`text-[11px] ${isCurrent ? 'font-semibold text-gray-900' : 'text-gray-500'}`}>
                    {item.label}
                  </p>
                </div>
              );
            })}
          </div>

          <div className="mb-6 text-center">
            <p className="text-sm text-gray-500">
              {step === 1 && "Enviaremos um código de 6 dígitos para o seu email."}
              {step === 2 && "Insira o código enviado para " + email}
              {step === 3 && "Crie uma nova senha segura para sua conta."}
              {step === 4 && "Sua senha foi redefinida com sucesso."}
            </p>
          </div>

          <div className="mt-6">
            {step === 1 && (
              <form className="space-y-5" onSubmit={handleEmail((v) => emailMutation.mutate(v))}>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium leading-6 text-gray-900">Email</label>
                  <div className="relative mt-2 rounded-md shadow-sm">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                      <EnvelopeIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                    </div>
                    <input
                      id="email" type="email" disabled={emailMutation.isPending}
                      className={clsx("block w-full rounded-md border-0 py-2.5 pl-10 text-gray-900 ring-1 ring-inset placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6 transition-all", errEmail.email ? "ring-red-400 focus:ring-red-500" : "ring-gray-300")}
                      placeholder="seu@email.com" {...regEmail("email")}
                    />
                  </div>
                  {errEmail.email && <p className="mt-2 text-sm text-red-600">{errEmail.email.message}</p>}
                </div>
                <button type="submit" disabled={emailMutation.isPending} className="flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 transition-colors disabled:opacity-70">
                  {emailMutation.isPending ? "Enviando..." : "Enviar Código"}
                </button>
              </form>
            )}

            {step === 2 && (
               <form className="space-y-5" onSubmit={handleOtp((v) => otpMutation.mutate(v))}>
                <div>
                  <label htmlFor="otp" className="block text-sm font-medium leading-6 text-gray-900">Código de 6 dígitos</label>
                  <div className="relative mt-2 rounded-md shadow-sm">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                      <KeyIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                    </div>
                    <input
                      id="otp" type="text" maxLength={6} disabled={otpMutation.isPending}
                      className={clsx("block w-full rounded-md border-0 py-2.5 pl-10 font-mono tracking-widest text-center text-gray-900 ring-1 ring-inset placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-lg sm:leading-6 transition-all", errOtp.otp ? "ring-red-400 focus:ring-red-500" : "ring-gray-300")}
                      placeholder="000000" {...regOtp("otp")}
                    />
                  </div>
                  {errOtp.otp && <p className="mt-2 text-sm text-red-600">{errOtp.otp.message}</p>}
                </div>
                <button type="submit" disabled={otpMutation.isPending} className="flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 transition-colors disabled:opacity-70">
                  {otpMutation.isPending ? "Verificando..." : "Verificar Código"}
                </button>
                <div className="text-center mt-4">
                   <button type="button" onClick={() => setStep(1)} className="text-sm font-medium text-indigo-600 hover:text-indigo-500 transition-colors">Voltar</button>
                </div>
              </form>
            )}

            {step === 3 && (
               <form className="space-y-5" onSubmit={handlePw((v) => passwordMutation.mutate(v))}>
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-xs text-gray-600">
                  A senha deve ter pelo menos 8 caracteres, com letras e números.
                </div>
                <div>
                  <label htmlFor="password" className="block text-sm font-medium leading-6 text-gray-900">Nova Senha</label>
                  <div className="relative mt-2 rounded-md shadow-sm">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                      <LockClosedIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                    </div>
                    <input
                      id="password" type="password" disabled={passwordMutation.isPending}
                      className={clsx("block w-full rounded-md border-0 py-2.5 pl-10 text-gray-900 ring-1 ring-inset placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6 transition-all", errPw.password ? "ring-red-400 focus:ring-red-500" : "ring-gray-300")}
                      placeholder="Sua nova senha" {...regPw("password")}
                    />
                  </div>
                  {errPw.password && <p className="mt-2 text-sm text-red-600">{errPw.password.message}</p>}
                </div>
                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium leading-6 text-gray-900">Confirmar Nova Senha</label>
                  <div className="relative mt-2 rounded-md shadow-sm">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                      <ShieldCheckIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                    </div>
                    <input
                      id="confirmPassword" type="password" disabled={passwordMutation.isPending}
                      className={clsx("block w-full rounded-md border-0 py-2.5 pl-10 text-gray-900 ring-1 ring-inset placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6 transition-all", errPw.confirmPassword ? "ring-red-400 focus:ring-red-500" : "ring-gray-300")}
                      placeholder="Confirme a nova senha" {...regPw("confirmPassword")}
                    />
                  </div>
                  {errPw.confirmPassword && <p className="mt-2 text-sm text-red-600">{errPw.confirmPassword.message}</p>}
                </div>
                <button type="submit" disabled={passwordMutation.isPending} className="flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 transition-colors disabled:opacity-70">
                  {passwordMutation.isPending ? "Redefinindo..." : "Redefinir Senha"}
                </button>
              </form>
            )}

            {step === 4 && (
              <div className="text-center">
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                  <CheckCircleIcon className="h-6 w-6 text-green-600" aria-hidden="true" />
                </div>
                <h3 className="mt-3 text-base font-semibold text-gray-900">Senha Alterada</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Sua senha foi redefinida com sucesso. Faça login para continuar.
                </p>
                <div className="mt-6">
                  <button type="button" onClick={() => navigate("/login")} className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 transition-colors">
                    Voltar para Login
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {step !== 4 && (
          <div className="mt-4 text-center">
            <Link to="/login" className="text-sm font-medium text-indigo-600 hover:text-indigo-500 transition-colors">
              Voltar para Login
            </Link>
          </div>
        )}
        
        {/* Version & Support Indicator */}
        <div className="mt-8 text-center">
          <p className="text-xs font-medium text-gray-400">Versão 0.1.0</p>
          {supportEmail && (
            <p className="mt-1 text-xs text-gray-400">
              Suporte:{' '}
              <a
                href={`mailto:${supportEmail}`}
                className="text-indigo-500 transition hover:text-indigo-600"
              >
                {supportEmail}
              </a>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
